import type { Query, UseQueryOptions } from '@tanstack/react-query';

import { queryOptions } from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import type { VestigasHTTPError } from '@vestigas/data';
import {
  fetchContractImportJobs,
  type PaginatedContractImportJobsResponse,
  queryKeysContractImportJob,
} from '~/data/contractImportJob';

import { getVirtualizedListPageQueryKey } from '@vestigas/ui';

type ContractImportJobsPage = Omit<
  PaginatedContractImportJobsResponse,
  'totalCount'
> & {
  readonly totalCount: number;
};

type ImportJobsQueryOptions<TPage, TError = Error> = Omit<
  UseQueryOptions<TPage, TError, TPage>,
  'queryKey' | 'queryFn'
>;

type ImportJobsPageWithData = Pick<PaginatedContractImportJobsResponse, 'data'>;

function hasInProgressImportJob(
  page: ImportJobsPageWithData | undefined,
): boolean {
  return page?.data.some((job) => job.status === 'in_progress') ?? false;
}

function getImportJobsRefetchInterval<
  TPage extends ImportJobsPageWithData,
  TError extends Error,
>(query: Query<TPage, TError>): number | false {
  return hasInProgressImportJob(query.state.data) ? ms('5s') : false;
}

export const importJobsQueryOptions = {
  refetchInterval: getImportJobsRefetchInterval,
} satisfies ImportJobsQueryOptions<ContractImportJobsPage, VestigasHTTPError>;

export const importJobsQueryKey = queryKeysContractImportJob.getAll();

export async function fetchImportJobsPage(
  offset: number,
  signal: AbortSignal,
): Promise<ContractImportJobsPage> {
  const response = await fetchContractImportJobs(
    {
      limit: PAGINATION_PAGE_SIZE_DEFAULT,
      offset,
    },
    { signal },
  );

  return {
    ...response,
    totalCount: response.totalCount ?? 0,
  };
}

/**
 * Options of the exact query the virtualized list keeps for one page, so an
 * observer outside the list shares its request and cache entry.
 */
export function getImportJobsPageQueryOptions(pageIndex: number) {
  return queryOptions({
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      fetchImportJobsPage(pageIndex * PAGINATION_PAGE_SIZE_DEFAULT, signal),
    queryKey: getVirtualizedListPageQueryKey({
      fetchPage: fetchImportJobsPage,
      pageIndex,
      pageSize: PAGINATION_PAGE_SIZE_DEFAULT,
      queryKey: importJobsQueryKey,
    }),
    refetchInterval: getImportJobsRefetchInterval,
    staleTime: ms('60s'),
  });
}

// The drawer needs page 0 before the list mounts (total count, empty state,
// collapsed counter). The in-progress refetch interval keeps page 0 polling
// while the drawer is mounted even when the virtualized list is not.
export const firstImportJobsPageQueryOptions = getImportJobsPageQueryOptions(0);
